"use client";

import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import {
  listMrNames,
  listTrainingSessions,
  type SessionSummary,
} from "@/lib/sessionsApi";

const DEFAULT_AGENT_ID = "sales_agent";

function formatDate(ts: number | null | undefined): string {
  if (ts == null || !Number.isFinite(ts)) return "—";
  return new Date(ts * 1000).toLocaleString("sl-SI", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(secs: number | null | undefined): string {
  if (secs == null || secs < 0) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function scoreColor(score: number | null | undefined): string {
  if (score == null) return "text-slate-500";
  if (score >= 8) return "text-emerald-400";
  if (score >= 6) return "text-amber-400";
  return "text-red-400";
}

export default function CoachPage() {
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [mrNames, setMrNames] = useState<string[]>([]);
  const [mrFilter, setMrFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, names] = await Promise.all([
        listTrainingSessions({
          agent_id: DEFAULT_AGENT_ID,
          mr_name: mrFilter.trim() || undefined,
          limit: 100,
        }),
        listMrNames(DEFAULT_AGENT_ID),
      ]);
      setSessions(list);
      setMrNames(names);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [mrFilter]);

  useEffect(() => {
    const t = setTimeout(() => {
      void load();
    }, mrFilter ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, mrFilter]);

  return (
    <AppShell>
      <main className="flex flex-col items-center p-6 gap-6">
        <header className="w-full max-w-5xl">
          <h1 className="text-2xl font-bold text-white mb-1">Coach — pregled sej</h1>
          <p className="text-sm text-slate-500">
            Read-only pregled zaključenih treningov in analiz po MR.
          </p>
        </header>

        <section
          className="w-full max-w-5xl rounded-2xl bg-slate-900 border border-slate-800 p-4 flex flex-wrap gap-3 items-end"
          aria-label="Filtri"
        >
          <div className="flex flex-col gap-1 min-w-[12rem] flex-1">
            <label htmlFor="mr-filter" className="text-xs font-medium text-slate-500">
              Filter po MR
            </label>
            <input
              id="mr-filter"
              type="search"
              list="mr-names-list"
              value={mrFilter}
              onChange={(e) => setMrFilter(e.target.value)}
              placeholder="Ime prodajalca…"
              className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            />
            <datalist id="mr-names-list">
              {mrNames.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg bg-slate-800 border border-slate-700 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            Osveži
          </button>
        </section>

        {error && (
          <p className="w-full max-w-5xl text-sm text-red-400" role="alert">
            {error}
          </p>
        )}

        <section
          className="w-full max-w-5xl rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden"
          aria-label="Seznam sej"
        >
          {loading ? (
            <p className="p-8 text-sm text-slate-500 text-center">Nalagam seje…</p>
          ) : sessions.length === 0 ? (
            <p className="p-8 text-sm text-slate-500 text-center">
              Ni shranjenih sej. MR naj zaključi trening z vnesenim imenom — poročilo
              se shrani po analizi.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-semibold">Datum</th>
                    <th className="px-4 py-3 font-semibold">MR</th>
                    <th className="px-4 py-3 font-semibold">HCP</th>
                    <th className="px-4 py-3 font-semibold">Trajanje</th>
                    <th className="px-4 py-3 font-semibold text-right">Ocena</th>
                    <th className="px-4 py-3 font-semibold sr-only">Akcija</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr
                      key={s.id}
                      className="border-b border-slate-800/80 hover:bg-slate-800/40 transition-colors"
                    >
                      <td className="px-4 py-3 text-slate-300 tabular-nums whitespace-nowrap">
                        {formatDate(s.ended_at)}
                      </td>
                      <td className="px-4 py-3 text-slate-200 font-medium">
                        {s.mr_name}
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {s.hcp_name ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-400 tabular-nums">
                        {formatDuration(s.duration_seconds)}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-bold tabular-nums ${scoreColor(s.overall_score)}`}
                      >
                        {s.overall_score != null ? s.overall_score.toFixed(1) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          href={`/coach/${s.id}`}
                          className="text-blue-400 hover:text-blue-300 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                        >
                          Poglej
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <p className="text-xs text-slate-600 text-center max-w-lg">
          {sessions.length > 0
            ? `${sessions.length} sej prikazanih (najnovejše najprej).`
            : "SSO ni potreben za coach view v1 — dostop je na ravni aplikacije."}
        </p>
      </main>
    </AppShell>
  );
}
